// Home "Review" tile (docs/SUBMISSION_FLOW_SPRINTS.md PR B) — first on the
// page because the hunt's bottleneck is packets that are built and never
// sent. Shows what's approved-but-unsent and what's ready to review; hides
// itself when neither exists.

import { Link } from "react-router";
import { ClipboardCheck } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardBody, CardHeader } from "../ui/Card";
import { useReviewQueue } from "../../lib/review";
import { useInsights } from "../../lib/insights";

export function ReviewQueueTile() {
  const queue = useReviewQueue();
  const cadence = useInsights().data?.cadence;
  const items = queue.data?.items ?? [];
  const approved = items.filter((i) => i.approval && !i.approval.stale);
  const ready = items.filter(
    (i) => (!i.approval || i.approval.stale) && i.extracted && i.counts.blockers === 0,
  );
  if (approved.length === 0 && ready.length === 0) return null;

  const oldest = Math.min(...[...approved, ...ready].map((i) => i.updated_at));
  const days = Math.floor((Date.now() - oldest) / 86_400_000);

  return (
    <Card className="border-accent-700/60">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <ClipboardCheck size={14} aria-hidden className="text-accent-300" />
            Ready to go out
          </span>
        }
        description={
          days >= 2 ? `The oldest has been waiting ${days} days.` : "Built and waiting on you."
        }
        action={
          <Link to="/review">
            <Button size="sm" variant="primary">
              Open review
            </Button>
          </Link>
        }
      />
      <CardBody className="space-y-1 text-sm text-neutral-300">
        {approved.length > 0 ? (
          <p>
            <span className="font-medium text-neutral-100">{approved.length}</span> approved, not
            yet submitted
          </p>
        ) : null}
        {ready.length > 0 ? (
          <p>
            <span className="font-medium text-neutral-100">{ready.length}</span> ready for your
            review
          </p>
        ) : null}
        {cadence ? (
          <p className="pt-1 text-xs text-neutral-500">
            This week: {cadence.this_week.submissions} of {cadence.target_per_week} sent ·{" "}
            {cadence.this_week.warm_touches} warm{" "}
            {cadence.this_week.warm_touches === 1 ? "touch" : "touches"}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
